import { Button } from "@paribus/shared-ui";
import { AlertCircle, FileText, FileX } from "lucide-react";

import { DocumentStatus, STATUS_COPY } from "./constants";

interface DocumentAccessViewProps {
  status: DocumentStatus;
  onNavigateBack?: () => void;
}

export const DocumentAccessView = ({ status, onNavigateBack }: DocumentAccessViewProps) => {
  const statusCopy = STATUS_COPY[status] ?? STATUS_COPY.error;

  const renderLoadingState = () => (
    <div className="flex flex-col items-center">
      <div className="relative mb-8">
        <div className="absolute inset-0 animate-pulse rounded-2xl bg-primary opacity-20 blur-2xl" />
        <div className="relative rounded-2xl border border-primary-border bg-primary-subtle p-8 shadow-xl">
          <FileText className="h-20 w-20 animate-pulse text-primary" />
        </div>
      </div>
      <div className="space-y-3 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{statusCopy.title}</h1>
        <p className="max-w-md text-lg leading-relaxed text-muted-foreground">
          {statusCopy.description}
        </p>
        <div className="pt-4">
          <div className="flex items-center justify-center space-x-2">
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
            <div className="h-2 w-2 animate-bounce rounded-full bg-primary" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotFoundState = () => (
    <div className="flex flex-col items-center">
      <div className="relative mb-8">
        <div className="absolute inset-0 rounded-2xl bg-warning opacity-20 blur-2xl" />
        <div className="relative rounded-2xl border border-warning-border bg-warning-subtle p-8 shadow-xl">
          <div className="rounded-2xl bg-warning-subtle-strong/50 p-4">
            <FileX className="h-20 w-20 text-warning" />
          </div>
        </div>
      </div>
      <div className="space-y-4 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{statusCopy.title}</h1>
        <p className="max-w-md text-lg leading-relaxed text-muted-foreground">
          {statusCopy.description}
        </p>
        {onNavigateBack && (
          <div className="pt-6">
            <Button
              onClick={onNavigateBack}
              className="rounded-xl bg-primary px-8 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:bg-primary-hover hover:shadow-xl"
            >
              Back to Cases
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderErrorState = () => (
    <div className="flex flex-col items-center">
      <div className="relative mb-8">
        <div className="absolute inset-0 rounded-2xl bg-destructive opacity-20 blur-2xl" />
        <div className="relative rounded-2xl border border-destructive-border bg-destructive-subtle p-8 shadow-xl">
          <div className="rounded-2xl bg-destructive-subtle-strong/50 p-4">
            <AlertCircle className="h-20 w-20 text-destructive" />
          </div>
        </div>
      </div>
      <div className="space-y-4 text-center">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">{statusCopy.title}</h1>
        <p className="max-w-md text-lg leading-relaxed text-muted-foreground">
          {statusCopy.description}
        </p>
        {onNavigateBack && (
          <div className="pt-6">
            <Button
              onClick={onNavigateBack}
              className="rounded-xl bg-primary px-8 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:bg-primary-hover hover:shadow-xl"
            >
              Back to Cases
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary-subtle p-4 sm:p-6 lg:p-8">
      <div className="w-full max-w-lg">
        <div className="rounded-2xl border border-white/40 bg-card/90 p-10 shadow-[0_20px_60px_rgba(0,0,0,0.12)] backdrop-blur-2xl duration-300 animate-in fade-in zoom-in-95 sm:p-12">
          {status === "loading" && renderLoadingState()}
          {status === "not_found" && renderNotFoundState()}
          {status === "error" && renderErrorState()}
        </div>
      </div>
    </div>
  );
};
